import axios, { AxiosRequestConfig } from 'axios';
// config
import { HOST_API } from 'config';
// routes
import { PATH_PAGE } from 'routes/paths';
// utils
import retryInstance, { handleRetry } from 'utils/retry';
import history from 'utils/history';
// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: HOST_API,
});

axios.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    if (config.url && config.url.charAt(0) === '/') {
      config.url = `${HOST_API}${config.url}`;
    }
    const { accessToken } = JSON.parse(
      localStorage.getItem(process.env.REACT_APP_LOCAL_TOKEN!) || '{}'
    );
    if (config.headers === undefined) {
      config.headers = {};
    }
    config.headers.Authorization = `Bearer ${accessToken}`;

    // TODO: Apply logger or any prior action
    return config;
  },
  (error) => Promise.reject(error)
  // TODO: apply error logger
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.config.retry) return false;
    const status = error?.response?.status;
    if (status === 400) {
      return Promise.resolve(error.response);
    }
    if (status === 403 && window.location.pathname === PATH_PAGE.login) {
      return Promise.resolve(error.response);
    }
    if (error.code === 'ENCONNABORTED') {
      // if timeout, redirect to timeout
      //forwardTo('/timeout')
      const event = new Event('requestTimeout');
      document.dispatchEvent(event);
      const timeoutError = {
        timeout: true,
      };
      return Promise.reject(timeoutError);
    }
    if (status === 403) {
      // redirect to 404
      history.push(PATH_PAGE.page403);
    }
    if (status === 404) {
      // redirect to 404
      history.push(PATH_PAGE.page404);
    }
    if (status === 500) {
      // redirect to 500
      history.push(PATH_PAGE.page500);
    }
    if (status === 524) {
      // redirect to 500
      history.push(PATH_PAGE.page500);
    }
    return Promise.reject(error?.response);
  }
);

//GET REQUEST
export const sendGet = (api: string, options?: any, dataOnly: boolean = true) =>
  new Promise((resolve, reject) => {
    axios
      .get(api, options)
      .then((response) => {
        if (dataOnly) resolve(response?.data);
        else resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });

//Post REQUEST
export const sendPost = (api: string, payload: any, options?: any, dataOnly: boolean = true) =>
  new Promise((resolve, reject) => {
    axios
      .post(api, payload, options)
      .then((response) => {
        if (dataOnly) resolve(response?.data);
        else resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });

//Put REQUEST
export const sendPut = (api: string, payload: any, options?: any, dataOnly: boolean = true) =>
  new Promise((resolve, reject) => {
    let requestInstance = axiosInstance;
    if (options?.retry) {
      handleRetry(retryInstance);
      requestInstance = retryInstance;
    }
    requestInstance
      .put(api, payload, options)
      .then((response) => {
        if (dataOnly) resolve(response?.data);
        else resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });

export const sendPatch = (api: string, payload: any, options?: any, dataOnly: boolean = true) =>
  new Promise((resolve, reject) => {
    let requestInstance = axiosInstance;
    if (options?.retry) {
      handleRetry(retryInstance);
      requestInstance = retryInstance;
    }
    requestInstance
      .patch(api, payload, options)
      .then((response) => {
        if (dataOnly) resolve(response?.data);
        else resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });

//Delete REQUEST
export const sendDelete = (api: string, options?: any, dataOnly: boolean = true) =>
  new Promise((resolve, reject) => {
    let requestInstance = axiosInstance;
    if (options?.retry) {
      handleRetry(retryInstance);
      requestInstance = retryInstance;
    }
    requestInstance
      .delete(api, options)
      .then((response) => {
        if (dataOnly) resolve(response?.data);
        else resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });

export default axiosInstance;
